import {
  WheelLockKeyCondition,
  WheelLockKeyLocationKind,
  WheelLockKeyMatch,
  WheelLockPresence,
  WheelLockScope,
  type WheelLockDetailRecord,
  type WheelLockSetupPatch
} from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiSheet, useAppForm } from 'noddi-ui-web';
import {
  WHEEL_LOCK_KEY_CONDITION_OPTIONS,
  WHEEL_LOCK_KEY_LOCATION_OPTIONS,
  WHEEL_LOCK_KEY_MATCH_OPTIONS,
  WHEEL_LOCK_PRESENCE_OPTIONS,
  wheelLockSetupFull,
  wheelLockSetupPatch,
  type WheelLockWriteFields
} from 'noddi-util';
import { useEffect } from 'react';
import { z } from 'zod';

const FORM_ID = 'edit-wheel-lock-form';

const schema = z.object({
  presence: z.enum([WheelLockPresence.UNKNOWN, WheelLockPresence.PRESENT, WheelLockPresence.ABSENT]),
  keyCondition: z.enum([
    WheelLockKeyCondition.UNKNOWN,
    WheelLockKeyCondition.AVAILABLE,
    WheelLockKeyCondition.MISSING,
    WheelLockKeyCondition.DAMAGED
  ]),
  keyLocationKind: z.enum([
    WheelLockKeyLocationKind.UNKNOWN,
    WheelLockKeyLocationKind.GLOVEBOX,
    WheelLockKeyLocationKind.BOOT,
    WheelLockKeyLocationKind.CUSTOMER,
    WheelLockKeyLocationKind.WORKSHOP
  ]),
  keyMatch: z.enum([WheelLockKeyMatch.UNKNOWN, WheelLockKeyMatch.CONFIRMED_FOR_SCOPE, WheelLockKeyMatch.NOT_CONFIRMED]),
  keyLocationNote: z.string(),
  manufacturer: z.string(),
  notes: z.string(),
  replacementCode: z.string()
});

type FormValues = z.input<typeof schema>;

export type WheelLockEditTarget =
  | { kind: 'car'; carId: number; title: string; description: string; lock: WheelLockDetailRecord }
  | {
      kind: 'wheelSet';
      carId: number;
      wheelSetId: number;
      title: string;
      description: string;
      lock: WheelLockDetailRecord;
    };

type Props = {
  target: WheelLockEditTarget | null;
  onClose: () => void;
};

/** A car editor writes the car. A wheel-set editor writes that set. */
const editorScope = (target: WheelLockEditTarget): WheelLockScope =>
  target.kind === 'car' ? WheelLockScope.CAR : WheelLockScope.WHEEL_SET;

/**
 * A confirmation belongs to the scope that recorded it. A car confirmation shown on a wheel set
 * must not be saved as a confirmation of that set.
 */
const formFrom = (target: WheelLockEditTarget): FormValues => {
  const lock = target.lock;
  const confirmationAppliesHere =
    lock.keyMatch !== WheelLockKeyMatch.CONFIRMED_FOR_SCOPE || lock.keyMatchScope === editorScope(target);
  return {
    presence: lock.presence,
    keyCondition: lock.keyCondition,
    keyLocationKind: lock.keyLocationKind,
    keyMatch: confirmationAppliesHere ? lock.keyMatch : WheelLockKeyMatch.UNKNOWN,
    keyLocationNote: lock.keyLocationNote,
    manufacturer: lock.manufacturer,
    notes: lock.notes,
    replacementCode: lock.replacementCode
  };
};

const writeFrom = (target: WheelLockEditTarget, value: FormValues): WheelLockWriteFields => ({
  ...formFrom(target),
  ...value,
  keyMatchScope: target.lock.keyMatchScope
});

/** A car editor owns a car row. A wheel-set editor owns a wheel-set row. A fallback is not that row. */
const ownsRow = (target: WheelLockEditTarget): boolean =>
  target.kind === 'car' ? target.lock.scope === WheelLockScope.CAR : target.lock.scope === WheelLockScope.WHEEL_SET;

const patchFor = (target: WheelLockEditTarget, value: FormValues): WheelLockSetupPatch | null => {
  const current = writeFrom(target, formFrom(target));
  const next = writeFrom(target, value);
  if (!ownsRow(target)) {
    return wheelLockSetupFull(next);
  }
  return wheelLockSetupPatch(current, next);
};

/**
 * Staff editor for one scope. Omitted fields stay. Blank text clears. A choice cannot be cleared.
 * The car write does not replace a wheel-set record, and a wheel-set write does not replace the car.
 */
export const EditWheelLockSheet = ({ target, onClose }: Props) => {
  const lock = target?.lock;
  const starting = target ? formFrom(target) : emptyValues();
  const { mutateAsync: patchCar, error: carError } = noddiAsync.usePatch({
    type: URLKeys.patchCarDetails,
    queryConfig: {
      meta: { suppressErrorToast: true },
      onSuccess: () => {
        if (target) {
          invalidateQueryExactMatch({ urlKey: URLKeys.getCar, input: { carId: target.carId } });
        }
      }
    }
  });
  const { mutateAsync: patchWheelSet, error: wheelSetError } = noddiAsync.usePatch({
    type: URLKeys.patchCarWheelSet,
    queryConfig: {
      meta: { suppressErrorToast: true },
      onSuccess: () => {
        if (target?.kind === 'wheelSet') {
          invalidateQueryExactMatch({ urlKey: URLKeys.getCar, input: { carId: target.carId } });
          invalidateQueryExactMatch({ urlKey: URLKeys.getCarWheelSet, input: { pk: target.wheelSetId } });
        }
      }
    }
  });
  const error = target?.kind === 'wheelSet' ? wheelSetError : carError;

  const form = useAppForm({
    defaultValues: starting,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      if (!target) {
        return;
      }
      const wheelLock = patchFor(target, value);
      if (!wheelLock) {
        onClose();
        return;
      }
      if (target.kind === 'car') {
        await patchCar({ carId: target.carId, wheelLock }, { onSuccess: onClose });
        return;
      }
      await patchWheelSet({ pk: target.wheelSetId, wheelLock }, { onSuccess: onClose });
    }
  });

  useEffect(() => {
    if (target) {
      form.reset(formFrom(target));
    }
  }, [target, form]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={target !== null}
        onOpenChange={(next) => {
          if (!next) {
            onClose();
          }
        }}
        title={target?.title ?? 'Edit wheel locks'}
        description={target?.description}
        footer={
          lock ? (
            <form.Subscribe selector={(state) => (target ? patchFor(target, state.values) : null)}>
              {(patch) => (
                <form.SubscribeButton
                  formId={FORM_ID}
                  label='Save changes'
                  disabled={patch === null}
                  disabledReason={patch === null ? 'Nothing to save' : undefined}
                />
              )}
            </form.Subscribe>
          ) : (
            <form.SubscribeButton formId={FORM_ID} label='Save changes' disabled disabledReason='Nothing to save' />
          )
        }
      >
        {error && (
          <div role='alert'>
            <ApiErrorMessageInternal error={error} />
          </div>
        )}
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <p className='text-sm text-greyMuted'>
            Blank text clears that field. A choice cannot be cleared. Available does not mean the key matches.
          </p>
          <form.AppField name='presence'>
            {(field) => (
              <field.Select
                label='Presence'
                options={WHEEL_LOCK_PRESENCE_OPTIONS}
                tooltip='Whether a wheel lock is fitted on the wheel set or car you are editing.'
              />
            )}
          </form.AppField>
          <form.AppField name='keyCondition'>
            {(field) => (
              <field.Select
                label='Key'
                options={WHEEL_LOCK_KEY_CONDITION_OPTIONS}
                tooltip='The condition of the key. Available means it is on hand. It does not mean the key matches the lock.'
              />
            )}
          </form.AppField>
          <form.AppField name='keyLocationKind'>
            {(field) => (
              <field.Select
                label='Key location'
                options={WHEEL_LOCK_KEY_LOCATION_OPTIONS}
                tooltip='Where the key is kept.'
              />
            )}
          </form.AppField>
          <form.AppField name='keyLocationNote'>
            {(field) => (
              <field.Input
                label='Location note'
                placeholder='Blank clears it'
                tooltip='Extra detail about where the key is kept.'
              />
            )}
          </form.AppField>
          <form.AppField name='keyMatch'>
            {(field) => (
              <field.Select
                label='Key match for this scope'
                options={WHEEL_LOCK_KEY_MATCH_OPTIONS}
                tooltip='Whether someone confirmed that this key opens the lock you are editing. Confirming the car does not confirm the other season.'
              />
            )}
          </form.AppField>
          <form.AppField name='manufacturer'>
            {(field) => (
              <field.Input label='Manufacturer' placeholder='Blank clears it' tooltip='Who made this lock.' />
            )}
          </form.AppField>
          <form.AppField name='notes'>
            {(field) => (
              <field.TextArea
                label='Notes'
                placeholder='Blank clears them'
                tooltip='Instructions for the next person who works on this lock.'
              />
            )}
          </form.AppField>
          <form.AppField name='replacementCode'>
            {(field) => (
              <field.Input
                label='Replacement code'
                placeholder='Blank clears it'
                autoComplete='off'
                tooltip='The code used to order a replacement key. Shown here for staff. Left out of lists, search, and the worker app.'
              />
            )}
          </form.AppField>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};

const emptyValues = (): FormValues => ({
  presence: WheelLockPresence.UNKNOWN,
  keyCondition: WheelLockKeyCondition.UNKNOWN,
  keyLocationKind: WheelLockKeyLocationKind.UNKNOWN,
  keyMatch: WheelLockKeyMatch.UNKNOWN,
  keyLocationNote: '',
  manufacturer: '',
  notes: '',
  replacementCode: ''
});
